import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import SEO from '../components/SEO';
import * as FiIcons from 'react-icons/fi';

const { FiArrowRight, FiBook, FiUsers, FiTrendingUp, FiAward, FiCheckCircle, FiDollarSign, FiHome, FiTarget } = FiIcons;

const Services = () => {
  const mentorshipTiers = [
    {
      id: 'bronze',
      title: 'Bronze',
      subtitle: 'Self-paced foundation',
      description: 'Core video course and materials that walk you through Tom\'s fundamentals so you can understand how real deals are structured from start to finish.',
      features: [
        'Full FlipAnythingUSA core course',
        'Entry-level access to community meetings',
        'Ideal if you are early and refining your strategy'
      ],
      icon: FiBook,
      gradient: 'from-amber-600 to-amber-800',
      ring: 'ring-amber-500/20'
    },
    {
      id: 'silver',
      title: 'Silver',
      subtitle: 'Foundation + extra support',
      description: 'Everything in Bronze plus additional group meetings where you can ask Tom questions live and see how other investors are structuring deals.',
      features: [
        'Core course + extended group sessions',
        '1–2 mentorship meetings per month',
        'Perfect if you want guidance while closing early deals'
      ],
      icon: FiUsers,
      gradient: 'from-slate-400 to-slate-600',
      ring: 'ring-slate-400/20'
    },
    {
      id: 'gold',
      title: 'Gold',
      subtitle: 'Action-focused group mentorship',
      description: 'Gold includes everything from the lower tiers plus direct access to Tom during regular live calls and support while you work through your own active deals.',
      features: [
        'Live group Zoom mentorship with Tom',
        'Access to a large archive of past mentorship calls',
        'Priority support for time-sensitive deal questions'
      ],
      icon: FiTrendingUp,
      gradient: 'from-yellow-500 to-amber-600',
      ring: 'ring-yellow-500/20',
      popular: true
    },
    {
      id: 'platinum',
      title: 'Platinum',
      subtitle: 'High-touch mentorship for serious investors',
      description: 'The most comprehensive access Tom offers, with deeper call archives, smaller group sessions, and more direct interaction for complex or larger deals.',
      features: [
        'All Gold benefits plus expanded archives',
        'Smaller, higher-level mentorship sessions',
        'Designed for scaling portfolios and larger projects'
      ],
      icon: FiAward,
      gradient: 'from-slate-700 to-slate-900',
      ring: 'ring-slate-500/20'
    }
  ];

  const whyChooseTom = [
    {
      title: '45-Year Mastery',
      description: 'I\'ve nearly done it all—land, commercial, industrial, office, apartments, trailer parks, and houses. This isn\'t textbook theory.',
      icon: FiHome
    },
    {
      title: 'Your Wealth Accelerator',
      description: 'My students skip the "rookie tax"—those $50,000 mistakes that wipe most new investors out.',
      icon: FiDollarSign
    },
    {
      title: 'The "Lifeline" Support',
      description: 'This isn\'t a digital course you watch alone. You get direct access to me—your "lifeline"—to navigate deals.',
      icon: FiTarget
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 transition-colors duration-300">
      <SEO
        title="Mentorship Programs"
        description="Real estate mentorship from a 45-year veteran investor. Choose from Bronze, Silver, Gold, or Platinum programs designed for every level of investor."
        path="/services"
      />

      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-center overflow-hidden bg-slate-900 dark:bg-slate-950">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&q=80&w=2000"
            alt="Real Estate"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-slate-900/80 dark:bg-slate-950/90" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-4xl mx-auto">
            <span className="inline-block text-emerald-400 uppercase tracking-[0.3em] text-xs md:text-sm font-bold mb-6">
              FlipAnythingUSA
            </span>
            <h1 className="text-5xl md:text-7xl font-serif font-bold mb-6 text-white">Mentorship Programs</h1>
            <p className="text-xl md:text-2xl text-slate-300 mb-10 font-light max-w-2xl mx-auto">
              Real-world real estate education from a 45-year investor. No hype. Just proven strategies that build wealth.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/onboarding" className="bg-emerald-500 text-white px-8 py-4 rounded-full font-semibold hover:bg-emerald-600 transition-all hover:scale-105 inline-flex items-center justify-center">
                Apply Now <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
              </Link>
              <a href="#tiers" className="bg-transparent border border-white/30 text-white px-8 py-4 rounded-full font-semibold hover:bg-white/10 transition-all backdrop-blur-sm">
                View Programs
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Tom Section */}
      <section className="py-24 bg-white dark:bg-slate-900 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-serif font-bold text-slate-900 dark:text-white mb-4">Why My Students Win</h2>
            <div className="h-1 w-20 bg-emerald-500 mx-auto rounded-full" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {whyChooseTom.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-slate-50 dark:bg-slate-800 p-8 rounded-2xl"
              >
                <div className="w-14 h-14 bg-emerald-500 rounded-xl flex items-center justify-center mb-6">
                  <SafeIcon icon={item.icon} className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4">{item.title}</h3>
                <p className="text-slate-600 dark:text-slate-400 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mentorship Tiers */}
      <section id="tiers" className="py-24 bg-slate-50 dark:bg-slate-800/50 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-emerald-500 font-bold tracking-widest uppercase text-sm mb-4 block">
              Choose Your Path
            </span>
            <h2 className="text-4xl font-serif font-bold text-slate-900 dark:text-white mb-4">Mentorship Tiers</h2>
            <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              Whether you're making your first offer or repositioning a portfolio, there's a mentorship level designed for where you are now — and where you want to go next.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {mentorshipTiers.map((tier, index) => (
              <motion.div
                key={tier.id}
                id={tier.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`relative bg-white dark:bg-slate-900 rounded-3xl overflow-hidden shadow-xl border-2 transition-all hover:-translate-y-2 ${tier.popular ? 'border-emerald-500' : 'border-transparent hover:border-emerald-500/30'}`}
              >
                {tier.popular && (
                  <div className="absolute top-4 right-4 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full z-10">
                    Most Popular
                  </div>
                )}

                <div className={`bg-gradient-to-br ${tier.gradient} p-8`}>
                  <div className={`w-14 h-14 bg-white/10 ${tier.ring} ring-4 rounded-xl flex items-center justify-center mb-4 backdrop-blur-sm`}>
                    <SafeIcon icon={tier.icon} className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-3xl font-serif font-bold text-white mb-2">{tier.title}</h3>
                  <p className="text-white/80">{tier.subtitle}</p>
                </div>

                <div className="p-8">
                  <p className="text-slate-600 dark:text-slate-400 mb-6">{tier.description}</p>
                  <ul className="space-y-4 mb-8">
                    {tier.features.map((feature, i) => (
                      <li key={i} className="flex items-start space-x-3">
                        <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700 dark:text-slate-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/onboarding"
                    className={`block text-center py-4 rounded-xl font-bold transition-all ${tier.popular ? 'bg-emerald-500 text-white hover:bg-emerald-600' : 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white hover:bg-slate-200 dark:hover:bg-slate-700'}`}
                  >
                    Apply for {tier.title}
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-24 bg-emerald-500 dark:bg-emerald-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-serif font-bold text-white mb-6">Real Results</h2>
          <p className="text-white/90 max-w-2xl mx-auto mb-12 text-lg">
            In just 5 years, Tom's mentees and students have generated a combined profit of over $20 Million. This isn't theory—it's proof.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
              <div className="text-5xl font-bold text-white mb-2">$20M+</div>
              <div className="text-white/80">Combined Student Profits</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
              <div className="text-5xl font-bold text-white mb-2">45+</div>
              <div className="text-white/80">Years of Experience</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
              <div className="text-5xl font-bold text-white mb-2">5</div>
              <div className="text-white/80">Years of Mentorship</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-900 dark:bg-slate-950 transition-colors duration-300">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-white mb-8">Ready to Build Your Empire?</h2>
            <p className="text-xl text-slate-300 mb-12 max-w-2xl mx-auto font-light">
              If you're serious about building real estate wealth with a mentor who has done it for decades, take the next step.
            </p>
            <Link to="/onboarding" className="bg-emerald-500 text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-emerald-600 transition-all hover:scale-105 inline-flex items-center">
              Apply for Mentorship <SafeIcon icon={FiArrowRight} className="ml-2 w-6 h-6" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;